<template>
  <div>
    <template v-if="showScopeSelector">
      <button-toggle-group
        v-model="localValue.scope"
        :items="toggleOptions"
        class="mb-4 flex-grow-1 w-100 scope-toggle"
        density="comfortable"
        data-testid="allow-block-scope-toggle"
      />
      <div
        v-if="isGlobal"
        class="warning-block px-3 py-1 mb-4 d-flex align-center"
        data-testid="allow-block-global-warning"
      >
        <v-icon class="mr-3" icon="$warning"></v-icon>
        <span class="body2" v-html="$t('modals.allowBlockEmail.globalWarning')" />
      </div>
    </template>
    <v-form ref="formRef" v-model="valid">
      <v-radio-group v-model="localValue.action" hide-details>
        <template v-for="action in actionsList" :key="action">
          <div class="mb-4">
            <v-radio
              class="body2 radio-button"
              :data-testid="`allow-block-email-modal-${action}-action`"
              :label="getLabel(action)"
              :value="action"
              :ripple="false"
              :disabled="isActionDisabled(action)"
            />
            <div class="ml-8 mt-1" v-if="showRecipientsConfig(action)">
              <div>
                <v-radio-group v-model="localValue.metadata.allRecipients" hide-details>
                  <v-radio
                    class="body2 mb-1"
                    data-testid="all-recipients-true"
                    :label="$t('modals.allowBlockEmail.recipientsActions.applyAll')"
                    :value="true"
                    :ripple="false"
                  />
                  <v-radio
                    class="body2"
                    data-testid="all-recipients-false"
                    :label="$t('modals.allowBlockEmail.recipientsActions.applySpecific')"
                    :value="false"
                    :ripple="false"
                  />
                </v-radio-group>
                <v-autocomplete
                  v-if="!localValue.metadata.allRecipients"
                  v-model="localValue.metadata.recipients"
                  data-testid="recipients-combobox"
                  class="mt-2 coro-scrollable-combobox"
                  :items="ticketDetails.sections?.emailMetaData?.proxyBlockedRecipients"
                  :label="$t('modals.allowBlockEmail.recipientsActions.label')"
                  :placeholder="$t('modals.allowBlockEmail.recipientsActions.placeholder')"
                  :rules="[atLeastOneIsRequired()]"
                  variant="outlined"
                  multiple
                  hide-no-data
                >
                  <template v-slot:chip="{ props, item }">
                    <v-chip
                      v-bind="props"
                      :closable="true"
                      variant="flat"
                      size="default"
                      close-icon="$closeCircle"
                      :text="item.title"
                      color="primary"
                    >
                      <div class="d-flex align-center">
                        {{ item.raw }}
                      </div>
                    </v-chip>
                  </template>
                  <template #item="{ props, item }">
                    <v-list-item v-bind="props" title="">
                      <div class="d-flex align-center" :data-testid="`item-${camelCase(item.raw)}`">
                        <span class="ml-1">{{ item.raw }}</span>
                      </div>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </div>
            </div>
          </div>
        </template>
      </v-radio-group>
    </v-form>
    <v-text-field
      v-model="localValue.description"
      class="mt-4"
      data-testid="allow-block-email-description-field"
      :label="$t('modals.addToAllowlist.descriptionLabel')"
      :placeholder="$t('modals.addToAllowlist.descriptionPlaceholder')"
      variant="outlined"
      maxlength="100"
      counter="100"
    />
    <template v-if="showCloseTicket">
      <v-divider class="mt-2 mb-6"></v-divider>
      <v-checkbox
        v-model="localValue.closeTicket"
        data-testid="close-ticket-checkbox"
        :label="$t('modals.allowBlockEmail.closeAllTicketsInGroup')"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";
import type { VuetifyFormRef } from "@/types.ts";
import { AllowBlockScope, TicketAction, TicketTrigger } from "@/constants/tickets.ts";
import intersection from "lodash/intersection";
import { Service } from "@/constants/cloud-apps.ts";
import { atLeastOneIsRequired, required } from "@/_helpers/validators.ts";
import camelCase from "lodash/camelCase";
import type { AllowBlockEmailFormData } from "@/_helpers/ticket-helpers.ts";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import ButtonToggleGroup from "@/components/ButtonToggleGroup.vue";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

const allowActions = [
  TicketAction.APPROVE_EMAIL,
  TicketAction.APPROVE_EMAILS_FROM_DOMAIN,
  TicketAction.APPROVE_EMAILS_FROM_SENDER,
  TicketAction.APPROVE_IP,
];

const blockActions = [
  TicketAction.DISCARD_EMAIL,
  TicketAction.DISCARD_EMAILS_FROM_DOMAIN,
  TicketAction.DISCARD_EMAILS_FROM_SENDER,
  TicketAction.DISCARD_IP,
];

export default defineComponent({
  components: { ButtonToggleGroup },
  props: {
    modelValue: {
      type: Object as PropType<AllowBlockEmailFormData>,
      required: true,
    },
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    ticketAction: {
      type: String as PropType<TicketAction>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showCloseTicket: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const appsManagementModule = useAppsManagementModule();
    const formRef = ref<VuetifyFormRef>();
    const valid = ref(false);

    const toggleOptions = [
      {
        value: AllowBlockScope.LOCAL,
        label: i18n.t("modals.allowBlockEmail.scopeSwitch.workspaceOnly"),
      },
      {
        value: AllowBlockScope.GLOBAL,
        label: i18n.t("modals.allowBlockEmail.scopeSwitch.allDescendants"),
      },
    ];

    const isAllowAction = computed(() => {
      return props.ticketAction === TicketAction.APPROVE_EMAIL_WITH_OPTIONS;
    });

    const actionsList = computed(() => {
      return isAllowAction.value ? allowActions : blockActions;
    });

    const enabledActions = computed(() => {
      if (isAllowAction.value) {
        return intersection(
          props.ticketDetails.actions.map((v) => v.name),
          allowActions
        );
      }
      return intersection(
        props.ticketDetails.actions.map((v) => v.name),
        blockActions
      );
    });

    const firstNonDisabledAction = actionsList.value.find((action) => !isActionDisabled(action));

    const localValue = ref<AllowBlockEmailFormData>({
      action: props.modelValue?.action || (firstNonDisabledAction as TicketAction),
      closeTicket: props.modelValue?.closeTicket || false,
      description: props.modelValue?.description,
      scope: props.modelValue?.scope,
      metadata: props.modelValue?.metadata || {
        allRecipients: true,
        recipients: [],
      },
    });

    const showScopeSelector = computed(() => {
      return (
        appsManagementModule.isMspContext &&
        !isMspActionRestricted(
          MspRolePermissionScopes.MSP_PORTAL_SCOPE,
          MspPortalScopeSection.MSP_ALLOW_BLOCK_LISTS
        )
      );
    });

    const isGlobal = computed(() => localValue.value.scope === AllowBlockScope.GLOBAL);

    function isActionDisabled(action: TicketAction) {
      return !enabledActions.value.includes(action) || props.disabled;
    }

    const getLabel = (action: string) => {
      const isProxy = props.ticketDetails.sections.serviceName === Service.PROXY;
      const isMalwareInEmailAttachment =
        props.ticketDetails.eventTrigger === TicketTrigger.MALWARE_IN_EMAIL_ATTACHMENTS;
      const proxyModeTranslationKey =
        props.ticketDetails.sections?.emailMetaData?.proxyBlockingMode ?? "default";

      if (isProxy) {
        return i18n.t(
          `modals.allowBlockEmail.proxyActions.${proxyModeTranslationKey}.${action}.title`
        );
      } else if (isMalwareInEmailAttachment) {
        return i18n.t(
          `modals.allowBlockEmail.malwareActions.${proxyModeTranslationKey}.${action}.title`
        );
      }

      return i18n.t(`modals.allowBlockEmail.actions.${proxyModeTranslationKey}.${action}.title`);
    };

    const isInboundProxy = computed(() => {
      return props?.ticketDetails?.sections?.serviceName === Service.PROXY;
    });

    const showRecipientsConfig = (action: TicketAction) => {
      return (
        localValue.value.action === TicketAction.APPROVE_EMAIL &&
        action === TicketAction.APPROVE_EMAIL &&
        isInboundProxy.value
      );
    };

    const validate = async () => {
      const isValid = await formRef.value!.validate();
      return isValid;
    };

    watch(
      localValue,
      (newValue) => {
        emit("update:modelValue", newValue);
      },
      { deep: true, immediate: true }
    );

    return {
      showRecipientsConfig,
      validate,
      valid,
      localValue,
      getLabel,
      actionsList,
      isActionDisabled,
      atLeastOneIsRequired,
      required,
      camelCase,
      formRef,
      isAllowAction,
      enabledActions,
      showScopeSelector,
      isGlobal,
      toggleOptions,
      AllowBlockScope,
      TicketAction,
    };
  },
});
</script>

<style scoped lang="scss">
.scope-toggle {
  height: 50px;
  :deep(.v-btn.toggle-button) {
    .v-btn__content {
      white-space: normal;
    }
  }
}
</style>
